import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle, ArrowRight, Info, Loader2, Trash2 } from 'lucide-react';
import api from '../../services/api';

const ENTITY_MAP  = { usa: 'Eyvo USA', india: 'Eyvo India' };
const ACCEPTED    = '.ofx,.qbo,.qfx,.csv';
const VALID_EXTS  = ['ofx', 'qbo', 'qfx', 'csv'];

const EXT_COLOR = {
  ofx: 'bg-blue-100 text-blue-800',
  qbo: 'bg-emerald-100 text-emerald-800',
  qfx: 'bg-teal-100 text-teal-800',
  csv: 'bg-amber-100 text-amber-800',
};

function extOf(name) { return name.split('.').pop().toLowerCase(); }
function fmtKB(bytes) { return `${(bytes / 1024).toFixed(1)} KB`; }

// status: idle | uploading | done | error
const makeEntry = (file) => ({
  id: `${file.name}-${file.size}-${Date.now()}`,
  file,
  ext: extOf(file.name),
  status: 'idle',
  result: null,
  error: '',
});

export default function BankImport() {
  const { pathname } = useLocation();
  const entitySlug   = pathname.split('/')[2];
  const entity       = ENTITY_MAP[entitySlug] || entitySlug;
  const navigate     = useNavigate();

  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState('');
  const [entries, setEntries]   = useState([]);   // { id, file, ext, status, result, error }
  const [dragging, setDragging] = useState(false);
  const [running, setRunning]   = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/api/bank-import/bank-accounts?entity=${encodeURIComponent(entity)}`)
      .then(r => setBankAccounts(r.data))
      .catch(console.error);
  }, [entity]);

  // ── file management ─────────────────────────────────────────────────────────
  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList)
      .filter(f => VALID_EXTS.includes(extOf(f.name)))
      .map(makeEntry);
    if (incoming.length < fileList.length) {
      // silently skip unsupported, they'll just not appear
    }
    setEntries(prev => {
      const existingNames = new Set(prev.map(e => e.file.name));
      return [...prev, ...incoming.filter(e => !existingNames.has(e.file.name))];
    });
  }, []);

  const removeEntry = (id) => setEntries(prev => prev.filter(e => e.id !== id));
  const clearAll    = ()  => setEntries([]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  // ── upload ───────────────────────────────────────────────────────────────────
  const patchEntry = (id, patch) =>
    setEntries(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));

  const submit = async () => {
    const pending = entries.filter(e => e.status === 'idle');
    if (!pending.length) return;
    if (!bankAccountId) {
      alert('Please select which bank account these statements are from.');
      return;
    }
    setRunning(true);
    for (const entry of pending) {
      patchEntry(entry.id, { status: 'uploading' });
      try {
        const fd = new FormData();
        fd.append('file', entry.file);
        fd.append('bank_account_id', bankAccountId);
        const res = await api.post(`/api/bank-import/${entitySlug}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        patchEntry(entry.id, { status: 'done', result: res.data });
      } catch (err) {
        patchEntry(entry.id, {
          status: 'error',
          error: err.response?.data?.detail || 'Upload failed',
        });
      }
    }
    setRunning(false);
  };

  // ── derived totals ────────────────────────────────────────────────────────
  const doneEntries = entries.filter(e => e.status === 'done');
  const totalImported  = doneEntries.reduce((s, e) => s + (e.result?.created   || 0), 0);
  const totalSkipped   = doneEntries.reduce((s, e) => s + (e.result?.skipped   || 0), 0);
  const totalParsed    = doneEntries.reduce((s, e) => s + (e.result?.total_parsed || 0), 0);
  const hasErrors      = entries.some(e => e.status === 'error');
  const allDone        = entries.length > 0 && entries.every(e => e.status === 'done' || e.status === 'error');
  const idleCount      = entries.filter(e => e.status === 'idle').length;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4" data-testid="bank-import-page">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Import Bank Transactions
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {entity} — drop multiple files at once to process them in a single run
        </p>
      </div>

      {/* Info */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-5 flex gap-3">
        <Info size={14} className="text-indigo-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-indigo-800 leading-relaxed space-y-0.5">
          <p><strong>Batch upload:</strong> Select or drag multiple files — all formats mixed in one drop.</p>
          <p><strong>Duplicates:</strong> Any transaction already in the system is skipped automatically.</p>
          <p><strong>AI categorization</strong> runs on import; known vendor rules are applied instantly.</p>
        </div>
      </div>

      {/* Bank account */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Bank account these statements are from <span className="text-red-500">*</span>
        </label>
        <select value={bankAccountId} onChange={e => setBankAccountId(e.target.value)}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          data-testid="bank-account-select">
          <option value="">— Select account —</option>
          {bankAccounts.map(a => (
            <option key={a.account_id} value={a.account_id}>
              {a.account_name} ({a.account_subtype})
            </option>
          ))}
        </select>
        <p className="text-xs text-slate-400 mt-1.5">Used as the bank/cash leg of every journal entry created.</p>
      </div>

      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer mb-4
          ${dragging
            ? 'border-indigo-400 bg-indigo-50'
            : entries.length
              ? 'border-slate-300 bg-slate-50/60 hover:border-indigo-300'
              : 'border-slate-300 bg-slate-50 hover:border-indigo-300 hover:bg-indigo-50/40'}`}
        onDrop={onDrop}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
        onClick={() => fileRef.current?.click()}
        data-testid="import-drop-zone"
      >
        <Upload size={30} className={`mx-auto mb-2 transition-colors ${dragging ? 'text-indigo-500' : 'text-slate-300'}`} />
        <p className="text-sm font-semibold text-slate-700">
          {dragging ? 'Drop files to add them' : 'Drag & drop files here, or click to browse'}
        </p>
        <p className="text-xs text-slate-400 mt-1">You can select multiple files at once</p>
        <div className="flex justify-center gap-1.5 mt-3">
          {['ofx', 'qbo', 'qfx', 'csv'].map(ext => (
            <span key={ext} className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wide ${EXT_COLOR[ext]}`}>
              {ext.toUpperCase()}
            </span>
          ))}
        </div>
      </div>

      <input ref={fileRef} type="file" accept={ACCEPTED} multiple className="hidden"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
        data-testid="import-file-input" />

      {/* File queue */}
      {entries.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">{entries.length} file{entries.length !== 1 ? 's' : ''} queued</span>
            {!running && <button onClick={clearAll} className="text-xs text-slate-400 hover:text-red-500">Clear all</button>}
          </div>
          <div className="divide-y divide-slate-50">
            {entries.map(entry => (
              <div key={entry.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`file-entry-${entry.ext}`}>

                {/* Status icon */}
                <div className="flex-shrink-0 w-5 text-center">
                  {entry.status === 'idle'      && <FileText size={15} className="text-slate-300" />}
                  {entry.status === 'uploading' && <Loader2 size={15} className="text-indigo-500 animate-spin" />}
                  {entry.status === 'done'      && <CheckCircle size={15} className="text-emerald-500" />}
                  {entry.status === 'error'     && <XCircle size={15} className="text-red-500" />}
                </div>

                {/* Name + size */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-800 truncate">{entry.file.name}</p>
                  <p className="text-[10px] text-slate-400">
                    {fmtKB(entry.file.size)}
                    {entry.status === 'done' && entry.result && (
                      <span className="ml-2 text-emerald-600 font-semibold">
                        +{entry.result.created} imported, {entry.result.skipped} skipped
                      </span>
                    )}
                    {entry.status === 'error' && (
                      <span className="ml-2 text-red-500">{entry.error}</span>
                    )}
                  </p>
                </div>

                {/* Format badge */}
                <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${EXT_COLOR[entry.ext] || 'bg-slate-100 text-slate-600'}`}>
                  {entry.ext.toUpperCase()}
                </span>

                {/* Remove (idle only) */}
                {entry.status === 'idle' && !running && (
                  <button onClick={() => removeEntry(entry.id)} className="flex-shrink-0 text-slate-200 hover:text-red-400 transition-colors">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload button */}
      {idleCount > 0 && (
        <button onClick={submit} disabled={running || !bankAccountId}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-900 text-white text-sm font-semibold rounded-lg hover:bg-indigo-800 disabled:opacity-60 transition-colors"
          data-testid="import-submit-btn">
          {running
            ? <><Loader2 size={15} className="animate-spin" /> Processing…</>
            : <><Upload size={15} /> Import {idleCount} file{idleCount !== 1 ? 's' : ''}</>}
        </button>
      )}

      {/* Summary after all done */}
      {allDone && (
        <div className="mt-5 bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            {hasErrors
              ? <AlertCircle size={18} className="text-amber-500" />
              : <CheckCircle size={18} className="text-emerald-500" />}
            <h2 className="font-bold text-slate-900">
              {hasErrors ? 'Batch complete with errors' : 'Batch import complete'}
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center mb-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-2xl font-bold font-mono text-slate-900">{totalParsed}</p>
              <p className="text-xs text-slate-500 mt-0.5">Total Parsed</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-2xl font-bold font-mono text-emerald-700">{totalImported}</p>
              <p className="text-xs text-slate-500 mt-0.5">Imported</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-2xl font-bold font-mono text-amber-700">{totalSkipped}</p>
              <p className="text-xs text-slate-500 mt-0.5">Duplicates Skipped</p>
            </div>
          </div>
          {totalImported > 0 && (
            <div className="flex gap-3">
              <button onClick={() => { setEntries([]); }}
                className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50"
                data-testid="import-another-btn">
                Import More Files
              </button>
              <button onClick={() => navigate(`/accounts/${entitySlug}/pending-review`)}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-indigo-900 text-white text-sm font-semibold rounded-lg hover:bg-indigo-800"
                data-testid="goto-pending-btn">
                Review Pending <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
